import i18n from './i18n';
import { getAvailableLangs } from './availableLangs';

export { getAvailableLangs } from './availableLangs';

const LOCAL_STORAGE_LANGUAGE_KEY = 'lang';

export type AvailableLangs = 'ru' | 'en';

const AVAILABLE_LOCALES = {
    en: 'en',
    ru: 'ru',
};

const ALL_LANGUAGES_OPTIONS = [
    {
        label: 'Русский',
        value: AVAILABLE_LOCALES.ru,
    },
    {
        label: 'English',
        value: AVAILABLE_LOCALES.en,
    },
];

export const LANGUAGES_OPTIONS = ALL_LANGUAGES_OPTIONS.filter(option =>
    getAvailableLangs().includes(option.value as AvailableLangs),
);

const ERROR_PREFIX = 'web_error_';

const getFullLanguageName = (lang: string) => `${lang}-${lang.toUpperCase()}`;

const getUserLanguage = (): AvailableLangs | null => {
    if (window.navigator.language) {
        return window.navigator.language.split('-')[0] as AvailableLangs;
    }

    return null;
};

const getURILanguage = (): AvailableLangs | null => {
    const { pathname } = window.location;
    const fullENLanguage = getFullLanguageName(AVAILABLE_LOCALES.en);
    const fullRULanguage = getFullLanguageName(AVAILABLE_LOCALES.ru);
    if (pathname.includes(fullENLanguage)) return AVAILABLE_LOCALES.en as AvailableLangs;
    if (pathname.includes(fullRULanguage)) return AVAILABLE_LOCALES.ru as AvailableLangs;

    return null;
};

export const getLangDescription = (lang: AvailableLangs) => ({
    langKey: lang,
    langLabel: i18n.t(('web_lang_label_' + lang) as LocaleString),
    langDescription: i18n.t(('web_lang_description_' + lang) as LocaleString),
});

export const getLanguage = (): AvailableLangs => {
    const availableLangs = getAvailableLangs();

    if (availableLangs.length === 1) {
        const onlyLang = availableLangs[0];
        localStorage.setItem(LOCAL_STORAGE_LANGUAGE_KEY, onlyLang);

        return onlyLang;
    }

    const uriLanguage = getURILanguage();
    if (uriLanguage && availableLangs.includes(uriLanguage)) {
        localStorage.setItem(LOCAL_STORAGE_LANGUAGE_KEY, uriLanguage);

        return uriLanguage;
    }

    const langLS = localStorage.getItem(LOCAL_STORAGE_LANGUAGE_KEY);
    if (langLS && availableLangs.includes(langLS as AvailableLangs)) {
        return langLS as AvailableLangs;
    }

    const userLanguage = getUserLanguage();
    if (userLanguage && availableLangs.includes(userLanguage)) {
        return userLanguage;
    }

    return (availableLangs[0] ?? AVAILABLE_LOCALES.en) as AvailableLangs;
};

export interface LocaleKeyRegistry {}
// Переопределение в проекте через module augmentation, пример:
// `messenger/src/@types/i18n-locale-keys.d.ts`
// declare module '@web-chat-core/ui-kit/lib/localizer' {
//     interface LocaleKeyRegistry extends Record<ProjectLocaleKey, true> {}
// }
type RegisteredLocaleKey = keyof LocaleKeyRegistry;

export type LocaleString = [RegisteredLocaleKey] extends [never] ? string : RegisteredLocaleKey;

/**
 * @deprecated Используйте функцию `t('some_key')` из i18next вместо localizer('some_key'). Эта функция может быть удалена в будущих версиях.
 */
export const localizer = (textVar: LocaleString, ...theArgs: any[]): string => {
    let translation = i18n.t(textVar);

    if (theArgs.length) {
        theArgs.forEach((arg, index) => {
            const placeholder = new RegExp(`\\{${index}\\}`, 'g');
            translation = translation.replace(placeholder, String(arg));
        });
    } else {
        // Удаляем плейсхолдеры вида {0}, {1}, если не переданы аргументы
        translation = translation.replace(/\{\d+\}/g, '');
    }

    return translation;
};

export const errorLocalizer = (errorText: LocaleString | string): string => {
    const error = `${ERROR_PREFIX}${errorText.toLowerCase()}` as string;

    return i18n.t(error as LocaleString);
};
